import store from '../../state';
import { MushroomService, SPECIES_CATALOG, MushroomSpeciesInfo } from '../../services/MushroomService';

export class MushroomPanel {
  private container: HTMLElement | null = null;
  public onClose?: () => void;
  private summaryData: any = null;

  public setOnClose(cb: () => void) {
    this.onClose = cb;
  }

  public async init() {
    this.summaryData = await MushroomService.getSummary();
    if (this.container) {
      this.updateUi();
    }
  }

  public render(state: any): string {
    const mode = state.mushroomMode || 'current';
    const currentSpeciesId = state.mushroomSpecies || 'general_flush';
    const speciesInfo: MushroomSpeciesInfo = SPECIES_CATALOG[currentSpeciesId] || SPECIES_CATALOG['general_flush'];

    const poi = state.selectedPoi;
    const lat = poi ? poi.lat : (state.mapCenter ? state.mapCenter[1] : 45.52);
    const lon = poi ? poi.lng : (state.mapCenter ? state.mapCenter[0] : -122.67);
    const elev = poi && poi.elev ? poi.elev : 1250;

    // Simulated local flush score based on summary or coordinates
    const localScore = Math.min(100, Math.max(10, Math.round(55 + 30 * Math.sin(lat * 0.1) * Math.cos(lon * 0.1))));
    const stageInfo = MushroomService.getStageInfo(localScore);

    const isCurrent = mode === 'current';
    const isOutlook = mode === 'outlook';

    // Hotspot regions from summary
    const regions = this.summaryData?.hotspot_regions || [
      { region: 'Pacific Northwest', status: 'Prime Harvest Window', species: ['Chanterelles', 'Porcini', 'Oysters'] },
      { region: 'Southern Appalachians', status: 'Peak Moisture Flush', species: ['Chicken of the Woods', 'Maitake', 'Chanterelles'] },
      { region: 'Upper Midwest & Great Lakes', status: 'Early Autumn Flush', species: ['Lion\'s Mane', 'Hen of the Woods'] }
    ];

    return `
      <div class="gw-mushroom-panel" style="display: flex; flex-direction: column; gap: 14px; padding: 14px; color: #fff; font-family: system-ui, -apple-system, sans-serif; font-size: 0.82rem;">
        
        <!-- Header -->
        <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(255,255,255,0.12); padding-bottom: 10px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 1.35rem;">🍄</span>
            <div>
              <div style="font-size: 0.95rem; font-weight: 800; letter-spacing: 0.02em; color: #ffffff;">Wild Mushroom Foraging</div>
              <div style="font-size: 0.70rem; color: var(--text-muted, #94a3b8);">Hydrological Antecedent Index & Phenology</div>
            </div>
          </div>
          <button class="gw-mushroom-close-btn" style="background: transparent; border: none; color: rgba(255,255,255,0.6); font-size: 1.2rem; cursor: pointer; padding: 4px 8px; border-radius: 4px;">✕</button>
        </div>

        <!-- Mode Switcher (Current Harvest vs 7-Day Outlook) -->
        <div style="display: flex; background: rgba(15, 23, 42, 0.7); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; padding: 3px; gap: 4px;">
          <button 
            class="gw-mushroom-mode-btn ${isCurrent ? 'active' : ''}" 
            data-mode="current"
            style="flex: 1; padding: 7px 10px; font-size: 0.75rem; font-weight: 700; border-radius: 6px; border: none; cursor: pointer; transition: all 0.2s ease; background: ${isCurrent ? 'linear-gradient(135deg, #ff9800, #f57c00)' : 'transparent'}; color: ${isCurrent ? '#fff' : 'rgba(255,255,255,0.65)'}; box-shadow: ${isCurrent ? '0 2px 8px rgba(255,152,0,0.35)' : 'none'};">
            🌧️ Current Harvest (Past 14d)
          </button>
          <button 
            class="gw-mushroom-mode-btn ${isOutlook ? 'active' : ''}" 
            data-mode="outlook"
            style="flex: 1; padding: 7px 10px; font-size: 0.75rem; font-weight: 700; border-radius: 6px; border: none; cursor: pointer; transition: all 0.2s ease; background: ${isOutlook ? 'linear-gradient(135deg, #0288d1, #01579b)' : 'transparent'}; color: ${isOutlook ? '#fff' : 'rgba(255,255,255,0.65)'}; box-shadow: ${isOutlook ? '0 2px 8px rgba(2,136,209,0.35)' : 'none'};">
            🔮 7-Day Ignition Outlook
          </button>
        </div>

        <!-- Species Preset Selector -->
        <div>
          <label style="display: block; font-size: 0.68rem; text-transform: uppercase; font-weight: 700; color: #cbd5e1; margin-bottom: 6px;">Target Species Preset</label>
          <select class="gw-mushroom-species-select" style="width: 100%; background: rgba(30, 41, 59, 0.85); color: #fff; border: 1px solid rgba(255,255,255,0.18); padding: 8px 10px; border-radius: 6px; font-size: 0.80rem; font-weight: 600; cursor: pointer; outline: none;">
            ${Object.values(SPECIES_CATALOG).map(sp => `
              <option value="${sp.id}" ${sp.id === currentSpeciesId ? 'selected' : ''}>
                ${sp.name}
              </option>
            `).join('')}
          </select>
        </div>

        <!-- Species Telemetry Card -->
        <div id="gw-mushroom-species-card" style="background: rgba(30, 41, 59, 0.6); border: 1px solid rgba(255,255,255,0.1); border-left: 4px solid ${speciesInfo.color}; border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 8px;">
          <div style="display: flex; justify-content: space-between; align-items: baseline;">
            <div id="gw-mushroom-species-name" style="font-weight: 800; font-size: 0.86rem; color: #fff;">${speciesInfo.name}</div>
            <div id="gw-mushroom-species-sci" style="font-size: 0.68rem; font-style: italic; color: #94a3b8;">${speciesInfo.scientificName}</div>
          </div>
          
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 0.72rem; padding-top: 4px;">
            <div>
              <span style="color: #94a3b8; display: block;">Primary Habitat:</span>
              <span id="gw-mushroom-species-host" style="font-weight: 600; color: #f1f5f9;">${speciesInfo.host}</span>
            </div>
            <div>
              <span style="color: #94a3b8; display: block;">Active Window:</span>
              <span id="gw-mushroom-species-season" style="font-weight: 600; color: #f1f5f9;">${speciesInfo.season}</span>
            </div>
            <div>
              <span style="color: #94a3b8; display: block;">Rainfall Trigger:</span>
              <span id="gw-mushroom-species-trigger" style="font-weight: 600; color: #f1f5f9;">${speciesInfo.rainTrigger}</span>
            </div>
            <div>
              <span style="color: #94a3b8; display: block;">Lag to Peak:</span>
              <span id="gw-mushroom-species-lag" style="font-weight: 600; color: #f1f5f9;">${speciesInfo.lagDays}</span>
            </div>
          </div>

          <div id="gw-mushroom-species-tips" style="font-size: 0.70rem; line-height: 1.35; color: #cbd5e1; background: rgba(0,0,0,0.25); padding: 6px 8px; border-radius: 4px; margin-top: 4px;">
            💡 <strong>Forager Tip:</strong> ${speciesInfo.tips}
          </div>
        </div>

        <!-- Selected Point Inspection Card -->
        <div style="background: rgba(15, 23, 42, 0.75); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 8px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div style="font-weight: 700; font-size: 0.75rem; text-transform: uppercase; color: #94a3b8;">Location Telemetry</div>
            <div style="font-size: 0.70rem; color: #64748b;">${lat.toFixed(3)}°N, ${Math.abs(lon).toFixed(3)}°W (${elev} ft)</div>
          </div>

          <div style="display: flex; align-items: center; justify-content: space-between; padding: 6px 0;">
            <div>
              <div style="font-size: 0.70rem; color: #94a3b8;">Local Flush Suitability</div>
              <div style="font-size: 1.35rem; font-weight: 900; color: ${stageInfo.color};">${localScore}<span style="font-size: 0.8rem; font-weight: 600; color: #94a3b8;"> / 100</span></div>
            </div>
            <div style="text-align: right;">
              <span style="display: inline-block; background: ${stageInfo.color}; color: #fff; padding: 4px 8px; border-radius: 12px; font-weight: 800; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.03em; box-shadow: 0 2px 6px rgba(0,0,0,0.4);">
                ${stageInfo.stage}
              </span>
            </div>
          </div>

          <div style="font-size: 0.70rem; color: #cbd5e1; border-top: 1px solid rgba(255,255,255,0.08); padding-top: 6px;">
            ${stageInfo.advice}
          </div>
        </div>

        <!-- Hotspot Leaderboard -->
        <div style="background: rgba(30, 41, 59, 0.4); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 10px;">
          <div style="font-size: 0.70rem; font-weight: 800; text-transform: uppercase; color: #94a3b8; margin-bottom: 6px;">
            Active Regional Flush Hotspots
          </div>
          <div style="display: flex; flex-direction: column; gap: 6px;">
            ${regions.map((r: any) => `
              <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.72rem; padding: 4px 6px; background: rgba(255,255,255,0.03); border-radius: 4px;">
                <div>
                  <div style="font-weight: 700; color: #fff;">${r.region}</div>
                  <div style="font-size: 0.64rem; color: #94a3b8;">${r.species.join(', ')}</div>
                </div>
                <span style="font-size: 0.65rem; font-weight: 700; color: #ff9800; background: rgba(255,152,0,0.15); padding: 2px 6px; border-radius: 4px;">
                  ${r.status}
                </span>
              </div>
            `).join('')}
          </div>
        </div>

        <!-- Safety Disclaimer -->
        <div style="font-size: 0.64rem; color: #94a3b8; line-height: 1.35; padding: 4px 2px; border-top: 1px solid rgba(255,255,255,0.08);">
          ⚠️ <strong>Forager Safety Rule:</strong> Never consume any wild mushroom unless you have 100% verified its identification with multiple reputable field guides or an expert mycologist.
        </div>

      </div>
    `;
  }

  public attachEvents(container: HTMLElement) {
    this.container = container;

    // Close button
    const closeBtn = container.querySelector('.gw-mushroom-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.onClose) {
          this.onClose();
        }
      });
    }

    // Mode buttons
    container.querySelectorAll('.gw-mushroom-mode-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const mode = (btn as HTMLElement).getAttribute('data-mode') as 'current' | 'outlook';
        if (mode) {
          const targetProduct = mode === 'outlook' ? 'mushrooms_outlook' : 'mushrooms';
          store.update({
            mushroomMode: mode,
            primaryProduct: targetProduct as any,
            activeLayerProducts: {
              ...store.getState().activeLayerProducts,
              mushrooms: true
            }
          });

          // In-place button style update to avoid re-rendering entire container
          container.querySelectorAll('.gw-mushroom-mode-btn').forEach(b => {
            const bMode = (b as HTMLElement).getAttribute('data-mode');
            const isActive = bMode === mode;
            const el = b as HTMLElement;
            if (bMode === 'current') {
              el.style.background = isActive ? 'linear-gradient(135deg, #ff9800, #f57c00)' : 'transparent';
              el.style.color = isActive ? '#fff' : 'rgba(255,255,255,0.65)';
              el.style.boxShadow = isActive ? '0 2px 8px rgba(255,152,0,0.35)' : 'none';
            } else {
              el.style.background = isActive ? 'linear-gradient(135deg, #0288d1, #01579b)' : 'transparent';
              el.style.color = isActive ? '#fff' : 'rgba(255,255,255,0.65)';
              el.style.boxShadow = isActive ? '0 2px 8px rgba(2,136,209,0.35)' : 'none';
            }
          });
        }
      });
    });

    // Species select
    const select = container.querySelector('.gw-mushroom-species-select') as HTMLSelectElement;
    if (select) {
      select.addEventListener('change', () => {
        const speciesId = select.value;
        // Update species telemetry card in-place without destroying the select element
        this.updateSpeciesCard(speciesId);
        // Defer store update so browser dropdown actor uninit can complete safely
        setTimeout(() => {
          store.update({ mushroomSpecies: speciesId });
        }, 0);
      });
    }
  }

  private updateSpeciesCard(speciesId: string) {
    if (!this.container) return;
    const speciesInfo: MushroomSpeciesInfo = SPECIES_CATALOG[speciesId] || SPECIES_CATALOG['general_flush'];
    const card = this.container.querySelector('#gw-mushroom-species-card') as HTMLElement;
    if (card) {
      card.style.borderLeft = `4px solid ${speciesInfo.color}`;
    }
    const nameEl = this.container.querySelector('#gw-mushroom-species-name');
    if (nameEl) nameEl.textContent = speciesInfo.name;
    const sciEl = this.container.querySelector('#gw-mushroom-species-sci');
    if (sciEl) sciEl.textContent = speciesInfo.scientificName;
    const hostEl = this.container.querySelector('#gw-mushroom-species-host');
    if (hostEl) hostEl.textContent = speciesInfo.host;
    const seasonEl = this.container.querySelector('#gw-mushroom-species-season');
    if (seasonEl) seasonEl.textContent = speciesInfo.season;
    const triggerEl = this.container.querySelector('#gw-mushroom-species-trigger');
    if (triggerEl) triggerEl.textContent = speciesInfo.rainTrigger;
    const lagEl = this.container.querySelector('#gw-mushroom-species-lag');
    if (lagEl) lagEl.textContent = speciesInfo.lagDays;
    const tipsEl = this.container.querySelector('#gw-mushroom-species-tips');
    if (tipsEl) tipsEl.innerHTML = `💡 <strong>Forager Tip:</strong> ${speciesInfo.tips}`;
  }

  public updateUi() {
    const cardBody = (this.container && this.container.classList.contains('legend-card-body'))
      ? this.container
      : (document.querySelector('.legend-card[data-layer="mushrooms"] .legend-card-body') as HTMLElement | null);
    if (!cardBody) return;
    const state = store.getState();
    cardBody.innerHTML = this.render(state);
    this.attachEvents(cardBody);
  }
}
